"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Heart } from "lucide-react";
import type { InteractionItemType } from "@gerama/api/core";
import { useSiteAuthStore } from "@gerama/stores";
import { cn } from "@gerama/utils";
import { browserCoreApi } from "@/lib/api";
import { openLogin } from "@/features/auth/login-modal";
import { loadInteractionStatus } from "./interaction-status";
import { LikeBurst, useLikeBurst } from "./like-burst";
import { invalidateLibrary } from "@/features/discover/library-keys";

export const likeKeys = {
  status: (itemType: InteractionItemType, itemId: number) =>
    ["interaction", "like", itemType, itemId] as const,
};

/**
 * The heart — Spotify's "save to Liked Songs". A guest tap opens the login
 * dialog. Optimistic; the server's answer wins.
 */
export function LikeButton({
  itemType,
  itemId,
  variant = "pill",
  className,
}: {
  itemType: InteractionItemType;
  itemId: number;
  /** `pill`: outlined button with a label; `icon`: bare heart (player bar). */
  variant?: "pill" | "icon";
  className?: string;
}) {
  const token = useSiteAuthStore((s) => s.token);
  const queryClient = useQueryClient();
  const key = likeKeys.status(itemType, itemId);

  const status = useQuery({
    queryKey: key,
    queryFn: () => loadInteractionStatus(itemType, itemId).then((s) => s.liked),
    enabled: !!token,
    staleTime: 60_000,
  });
  const liked = !!status.data;
  const [burst, fireBurst] = useLikeBurst();

  const toggle = useMutation({
    mutationFn: () => browserCoreApi.playback.toggleLike(itemType, itemId),
    onMutate: () => queryClient.setQueryData(key, !liked),
    onSuccess: (value) => {
      queryClient.setQueryData(key, value);
      // Liked songs/videos live in the library — show the change there right away.
      void invalidateLibrary(queryClient);
    },
    onError: () => queryClient.setQueryData(key, liked),
  });

  const onClick = () => {
    if (!token) {
      openLogin();
      return;
    }
    // Only liking celebrates; un-liking is quiet.
    if (!liked) fireBurst();
    toggle.mutate();
  };

  const label = liked ? "حذف از پسندیده‌ها" : "افزودن به پسندیده‌ها";

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={liked}
        title={label}
        className={cn(
          "transition-transform active:scale-90",
          liked ? "text-primary" : "text-muted-foreground hover:text-foreground",
          className,
        )}
      >
        <LikeBurst shot={burst} size={20}>
          <Heart className={cn("size-5", liked && "fill-current")} />
        </LikeBurst>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={liked}
      className={cn(
        "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
        liked
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border hover:border-foreground/40",
        className,
      )}
    >
      <LikeBurst shot={burst} size={16}>
        <Heart className={cn("size-4", liked && "fill-current")} />
      </LikeBurst>
      {liked ? "پسندیدی" : "پسندیدن"}
    </button>
  );
}
